import { useCallback, useEffect, useState } from 'react';

/**
 * URLハッシュとタブの状態を同期するカスタムフック
 *
 * @param defaultTab - デフォルトのタブキー
 * @param validTabs - 有効なタブキーの配列（オプション、指定時はハッシュの検証に使用）
 * @returns [activeTab, setActiveTab] - 現在のアクティブタブとタブを変更する関数
 *
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useHashTab('contract', ['contract', 'addons', 'invoices']);
 *
 * return (
 *   <Tabs
 *     activeKey={activeTab}
 *     onChange={setActiveTab}
 *     items={tabItems}
 *   />
 * );
 * ```
 */
export function useHashTab<T extends string = string>(
  defaultTab: T,
  validTabs?: readonly T[]
): [T, (tab: T) => void] {
  // URLハッシュからタブキーを取得する関数
  const getTabFromHash = useCallback((): T => {
    if (typeof window === 'undefined') {
      return defaultTab;
    }

    const hash = window.location.hash.slice(1); // '#'を除去

    if (!hash) {
      return defaultTab;
    }

    // 有効なタブが指定されている場合、検証する
    if (validTabs && validTabs.length > 0) {
      if (validTabs.includes(hash as T)) {
        return hash as T;
      }
      return defaultTab;
    }

    return hash as T;
  }, [defaultTab, validTabs]);

  const [activeTab, setActiveTabState] = useState<T>(() => getTabFromHash());

  // タブを変更する関数（URLハッシュも更新）
  const setActiveTab = useCallback((tab: T) => {
    setActiveTabState(tab);

    if (typeof window !== 'undefined') {
      const newUrl = `${window.location.pathname}${window.location.search}#${tab}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  // ブラウザの戻る/進むボタンでハッシュが変更された場合に対応
  useEffect(() => {
    const handleHashChange = () => {
      const tabFromHash = getTabFromHash();
      setActiveTabState(tabFromHash);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [getTabFromHash]);

  // 初回マウント時にハッシュからタブを復元
  useEffect(() => {
    const tabFromHash = getTabFromHash();
    if (tabFromHash !== defaultTab) {
      setActiveTabState(tabFromHash);
    }
  }, [defaultTab, getTabFromHash]);

  return [activeTab, setActiveTab];
}
